import { UseToastOptions } from "@chakra-ui/react"
import {
  EntityId,
  FulfillmentTypes,
  ImageFilterName,
  OrientationType,
  PrintServiceProductEntityHydrated,
  tObjectFit,
} from "@jackfruit/common"
import { createAction } from "@reduxjs/toolkit"
import { Snapshot } from "recoil"
import { Order } from "~/interfaces/entities/autopilot/Order"
import { UpdateOrderSummaryReasonType } from "./saga/processUpdateOrderSummary"
import { VNZAuth } from "./state/VNZAuth"
export interface UploadFilePayload {
  id: EntityId
  file: File
}
const queueImageForProcessing = createAction<UploadFilePayload>(
  "process/queueImageForProcessing"
)

const queueImageForUploading = createAction<UploadFilePayload>(
  "process/queueImageForUploading"
)

export interface SearchStorePayload {}
const searchStores = createAction<SearchStorePayload>("process/searchStores")

export interface InitStorePayload {
  printServiceId: EntityId
  productId: EntityId
}
const initStores = createAction<InitStorePayload>("process/initStores")

export interface PlaceOrderPayload {
  successUrl: string
}
const placeOrder = createAction<PlaceOrderPayload>("process/placeOrder")

export interface CreateOrderPayload {
  successUrl: string
}
const createOrder = createAction<CreateOrderPayload>("process/createOrder")

export interface UpdateOrderSummary {
  reason?: string
  reasonType?: UpdateOrderSummaryReasonType
}
const updateOrderSummary = createAction<UpdateOrderSummary>(
  "process/updateOrderSummary"
)

export interface UpdateOrderSummarySuccess {
  reasonType?: UpdateOrderSummaryReasonType
}
const updateOrderSummarySuccess = createAction<UpdateOrderSummarySuccess>(
  "process/updateOrderSummarySuccess"
)

export interface CouponCodePayload {}
const applyCouponCode = createAction<CouponCodePayload>("process/couponCode")

export interface ApplyCouponCodeSuccessPayload {}
const applyCouponCodeSuccess = createAction<ApplyCouponCodeSuccessPayload>(
  "process/applyCouponCodeSuccess"
)

export interface CreateUploadPayload {
  pageId: EntityId
  file: File
  scrollTo?: (to: string) => void
  nextBlockName?: string
  productId?: EntityId
}
export const createUpload = createAction<CreateUploadPayload>("uploads/create")

export interface CreateUploadFromButtonPayload {
  pageId: EntityId
  file: File
  fulfillment: FulfillmentTypes
  productIds?: EntityId[]
  productCategoryIds?: EntityId[]
}
const createUploadFromButton = createAction<CreateUploadFromButtonPayload>(
  "uploads/createFromButton"
)

export interface CreateUploadFromBuyNowButtonPayload {
  pageId: EntityId
  file: File
  fulfillment: FulfillmentTypes
  productId: EntityId
}

const createUploadFromBuyNowButton =
  createAction<CreateUploadFromBuyNowButtonPayload>(
    "uploads/createFromBuyNowButton"
  )

export interface CreateUploadFromFileResolverPayload {
  pageId: EntityId
  fileResolver: () => Promise<File>
  scrollTo?: (to: string) => void
  nextBlockName?: string
  productId?: EntityId
}
const createUploadFromFileResolver =
  createAction<CreateUploadFromFileResolverPayload>(
    "uploads/createFromFileResolver"
  )

export interface CreateLineItemPayload {
  productId: EntityId
  createFromProduct?: boolean
}
export interface CreateLineItemWithImagePayload extends CreateLineItemPayload {
  file: File
}

const createLineItem = createAction<CreateLineItemPayload>("lineItems/create")
const createLineItemWithImage = createAction<CreateLineItemWithImagePayload>(
  "lineItems/createWithImage"
)

export interface RemoveLineItemPayload {
  lineItemId: EntityId
}
const removeLineItem = createAction<RemoveLineItemPayload>("lineItems/remove")

export interface CreateLineItemFromTemplatePayload
  extends CreateLineItemPayload {
  productTemplateId: EntityId
  blockId?: EntityId
  blockTemplateId?: EntityId
  shouldOpenEditor?: boolean
}

const createLineItemFromTemplate =
  createAction<CreateLineItemFromTemplatePayload>(
    "lineItems/createFromTemplate"
  )

export interface DuplicateLineItemPayload {
  lineItemId: EntityId
}

const duplicateLineItem = createAction<DuplicateLineItemPayload>(
  "lineItems/duplicate"
)

export type UpdateLineItemProductFrom = "lineItemPreview" | "editor"
export interface UpdateLineItemProduct {
  lineItemId: EntityId
  productId: EntityId
  event: "productChanged" | "storeChanged" | "fulfillmentChanged"
  from?: UpdateLineItemProductFrom
}
const updateLineItemProduct = createAction<UpdateLineItemProduct>(
  "lineItems/updateProduct"
)

export interface UpdateLineItemProductTemplate {
  lineItemId: EntityId
  productTemplateId: EntityId
}
const updateLineItemProductTemplate =
  createAction<UpdateLineItemProductTemplate>("lineItems/updateProductTemplate")

export interface UpdateLineItemProductTemplateVariant {
  lineItemId: EntityId
  productTemplateVariantId: string
}
const updateLineItemProductTemplateVariant =
  createAction<UpdateLineItemProductTemplateVariant>(
    "lineItems/updateProductTemplateVariant"
  )

export interface UpdateImageRegionImage {
  imageRegionId: EntityId
  file: File
  imageFit?: tObjectFit
  isClampingEnabled?: boolean
  imageFilterName?: ImageFilterName
}
const updateImageRegionImage = createAction<UpdateImageRegionImage>(
  "imageRegions/updateImage"
)

export interface UpdateDocumentPayload {
  lineItemId: EntityId
  file: File
}
const updateDocument = createAction<UpdateDocumentPayload>(
  "lineItems/updateDocument"
)

export interface UpdateMissingImagesForLineItem extends DialogPayload {
  lineItemId: EntityId
  imageRegionId?: EntityId
  files: File[]
  onOpen?: (type?: string) => void
}
const updateMissingImagesForLineItem =
  createAction<UpdateMissingImagesForLineItem>(
    "lineItems/updateMissingImagesForLineItem"
  )

export interface SetUpdateMissingImagesForLineItemDialogChoice {
  choice: boolean
}
const setUpdateMissingImagesDialogChoice =
  createAction<SetUpdateMissingImagesForLineItemDialogChoice>(
    "lineItems/setUpdateMissingImagesDialogChoice"
  )

export interface UpdateLineItemOrientationPayload {
  lineItemId: EntityId
  orientation: OrientationType
}
const updateLineItemOrientation =
  createAction<UpdateLineItemOrientationPayload>("lineItems/updateOrientation")

export interface DownloadProductTemplates {
  blockTemplateId: EntityId
  preTags: EntityId[]
  tags?: EntityId[]
  limit?: number
  offset?: number
  keyword?: string
}
const downloadProductTemplates = createAction<DownloadProductTemplates>(
  "productTemplates/download"
)

export interface DownloadProductTemplate {
  id: EntityId
}
const downloadProductTemplate = createAction<DownloadProductTemplate>(
  "productTemplate/download"
)

export interface DownloadProductTemplateTags {
  tags: EntityId[]
}
const downloadProductTemplateTags = createAction<DownloadProductTemplateTags>(
  "productTemplateTags/download"
)

export interface ImageAITransformationPayload {
  lineItemId: EntityId
  imageTransformTypeId: string
}

const applyImageAITransformation = createAction<ImageAITransformationPayload>(
  "process/applyImageAITransformation"
)

interface DialogPayload {
  onOpen?: () => void
  onClose?: () => void
}

export interface CheckCompatibilityPayload extends DialogPayload {
  availableProducts: PrintServiceProductEntityHydrated[]
}

export interface ProductCompatibilityPayload {
  availableProducts: PrintServiceProductEntityHydrated[]
}

const productCompatibility = createAction<SearchStorePayload>(
  "process/productCompatibility"
)

export interface ProductCompatibilityDialogPayload {
  choice: boolean
}
const productCompatibilityDialog =
  createAction<ProductCompatibilityDialogPayload>(
    "productCompatibility/dialogChoice"
  )

export interface UpdateFulfillementPayload {
  pageId: EntityId
  fulfillment: FulfillmentTypes
  onOpen?: ({
    lineItemsToKeep,
    lineItemsToRemove,
  }: {
    lineItemsToKeep: EntityId[]
    lineItemsToRemove: EntityId[]
  }) => void
  onClose?: () => void
  callback?: () => void
}
const updateFulfillment = createAction<UpdateFulfillementPayload>(
  "process/updateFulfillment"
)

export interface UpdateFulfillmentSuccessPayload {}
const updateFulfillmentSuccess = createAction<UpdateFulfillmentSuccessPayload>(
  "process/updateFulfillmentSuccess"
)

const updatedFulfillmentForPage = (pageId: EntityId) =>
  createAction(`process/updatesFulfillment_${pageId}`)

export interface UpdateStorePayload {
  storeId: EntityId
  onOpen?: ({
    lineItemsToKeep,
    lineItemsToRemove,
  }: {
    lineItemsToKeep: EntityId[]
    lineItemsToRemove: EntityId[]
  }) => void
  onClose?: () => void
}
const updateStore = createAction<UpdateStorePayload>("process/updateStore")
const updatedStoreForPage = (pageId: EntityId) =>
  createAction(`process/updatedStoreForPage_${pageId}`)

export interface ApplyEditorChangesPayload {
  lineItemId: EntityId
  snapshot: Snapshot
}

const applyEditorChanges = createAction<ApplyEditorChangesPayload>(
  "process/applyEditorChanges"
)

interface addLineItemToCartPayload {
  lineItemId: EntityId
  cartId: EntityId
}

export const addLineItemToCart = createAction<addLineItemToCartPayload>(
  "process/addLineItemToCart"
)
const restoreCheckoutData = createAction("process/restoreCheckoutData")
const restoreCartData = createAction("process/restoreCartData")
const saveAttributionData = createAction("process/saveAttributionData")

export interface BootstrapPagePayload {
  id: EntityId
  toast: (options?: UseToastOptions) => void
}

const bootstrapPage = createAction<BootstrapPagePayload>(
  "process/bootstrapPage"
)

export interface ProcessCloudProviderSessionPayload {}

const processCloudProviderSession =
  createAction<ProcessCloudProviderSessionPayload>(
    "process/processCloudProviderSession"
  )

export interface PlaceReOrderPayload {
  order: Order
  onOpen: ({
    canReOrderSomeProducts,
    lineItemLabels,
  }: {
    canReOrderSomeProducts: boolean
    lineItemLabels: string[]
  }) => void
  onClose: () => void
}

const placeReOrder = createAction<PlaceReOrderPayload>("process/placeReOrder")

export interface OrderFromStorePayload {
  storePlace: string
  storeId: EntityId
  storeLat: number
  storeLng: number
}

export const orderFromStore = createAction<OrderFromStorePayload>(
  "process/orderFromStore"
)

export interface UploadUriImagesPayload {
  imageURIs: string[]
  pageId: EntityId
  toast: (options?: UseToastOptions) => void
  scrollTo?: (to: string) => void
  nextBlockName?: string
}

export const uploadUriImages = createAction<UploadUriImagesPayload>(
  "process/uploadUriImages"
)

export interface InsertPageSpreadToLineItemPayload {
  lineItemId: EntityId
  productPageId: EntityId
  callback?: (id: EntityId) => void
}

export const insertPageSpreadToLineItemBefore =
  createAction<InsertPageSpreadToLineItemPayload>(
    "process/insertPageSpreadToLineItemBefore"
  )

export const insertPageSpreadToLineItemAfter =
  createAction<InsertPageSpreadToLineItemPayload>(
    "process/insertPageSpreadToLineItemAfter"
  )

export interface RemovePageSpreadFromLineItemPayload {
  lineItemId: EntityId
  productPageId: EntityId
  callback?: (id: EntityId) => void
}

export const removePageSpreadFromLineItem =
  createAction<RemovePageSpreadFromLineItemPayload>(
    "process/removePageSpreadFromLineItem"
  )

export interface RemoveIncompletePageSpreadsFromLineItemPayload {
  lineItemId: EntityId
}

export const removeIncompletePageSpreadsFromLineItem =
  createAction<RemoveIncompletePageSpreadsFromLineItemPayload>(
    "process/removeIncompletePageSpreadsFromLineItem"
  )

export interface CreateLineItemFromButtonPayload {
  productId: EntityId
  currentFulfillment: FulfillmentTypes
  fulfillment: FulfillmentTypes
  onOpen?: ({
    lineItemsToKeep,
    lineItemsToRemove,
  }: {
    lineItemsToKeep: EntityId[]
    lineItemsToRemove: EntityId[]
  }) => void
  onClose?: () => void
}
export const createLineItemFromButton =
  createAction<CreateLineItemFromButtonPayload>(
    "process/createLineItemFromButton"
  )

export interface SwapImageRegionFromPageAToPageBPayload {
  pageAId: EntityId
  pageBId: EntityId
}

export const swapImageRegionFromPageAToPageB =
  createAction<SwapImageRegionFromPageAToPageBPayload>(
    "process/swapImageRegionFromPageAToPageB"
  )

export interface BootstrapProductFeedPayload {
  blockId: EntityId
  productId: EntityId
  templateId?: EntityId
}

const bootstrapProductFeed = createAction<BootstrapProductFeedPayload>(
  "process/bootstrapProductFeed"
)

export interface UploadImagesAndCreateLineItemsForProductIdPayload {
  productFeedBlockId?: EntityId
  productId: EntityId
}

export const uploadImagesAndCreateLineItemsForProductId =
  createAction<UploadImagesAndCreateLineItemsForProductIdPayload>(
    "process/uploadImagesAndCreateLineItemsForProductId"
  )

export interface CreateLineItemForProductIdWithPlaceholderImagePayload {
  productFeedBlockId?: EntityId
  productId: EntityId
}

export const createLineItemForProductIdWithPlaceholderImage =
  createAction<CreateLineItemForProductIdWithPlaceholderImagePayload>(
    "process/createLineItemForProductIdWithPlaceholderImage"
  )

export interface CreateLineItemForProductIdWithImageUrlPayload {
  productId?: EntityId
  imageUrls: (File | string)[]
  onComplete?: () => void
  skipAutoScroll?: boolean
}

export interface ReplaceImageRegionImageWithImageUrlPayload {
  imageRegionId: EntityId
  imageUrl: string
  onComplete?: () => void
}

export interface FillLineItemImageRegionsWithImageUrlsPayload {
  lineItemId: EntityId
  imageRegionId?: EntityId
  imageUrls: (File | string)[]
  onComplete?: () => void
}

export const createLineItemForProductIdWithImageUrl =
  createAction<CreateLineItemForProductIdWithImageUrlPayload>(
    "process/createLineItemForProductIdWithImageUrl"
  )

export const replaceImageRegionImageWithImageUrl =
  createAction<ReplaceImageRegionImageWithImageUrlPayload>(
    "process/replaceImageRegionImageWithImageUrl"
  )

export const fillLineItemImageRegionsWithImageUrls =
  createAction<FillLineItemImageRegionsWithImageUrlsPayload>(
    "process/fillLineItemImageRegionsWithImageUrls"
  )

export interface UploadImagesAndCreateLineItemsForProductIdAndTemplateIdPayload {
  productFeedBlockId?: EntityId
  productId: EntityId
  templateId: EntityId
}

export const uploadImagesAndCreateLineItemsForProductIdAndTemplateId =
  createAction<UploadImagesAndCreateLineItemsForProductIdAndTemplateIdPayload>(
    "process/uploadImagesAndCreateLineItemsForProductIdAndTemplateId"
  )

export interface CreateLineItemForProductIdAndTemplateIdWithPlaceholderImagePayload {
  productFeedBlockId?: EntityId
  productId: EntityId
  templateId: EntityId
}

export const createLineItemForProductIdAndTemplateIdWithPlaceholderImage =
  createAction<CreateLineItemForProductIdAndTemplateIdWithPlaceholderImagePayload>(
    "process/createLineItemForProductIdAndTemplateIdWithPlaceholderImage"
  )

export interface CreateLineItemForProductIdPayload {
  productFeedBlockId?: EntityId
  productId: EntityId
  shouldOpenEditor: boolean
}

export const createLineItemForProductId =
  createAction<CreateLineItemForProductIdPayload>(
    "process/createLineItemForProductId"
  )

export interface CreateLineItemForProductIdAndTemplateIdPayload {
  productFeedBlockId?: EntityId
  productId: EntityId
  templateId: EntityId
  shouldOpenEditor: boolean
}

export const createLineItemForProductIdAndTemplateId =
  createAction<CreateLineItemForProductIdAndTemplateIdPayload>(
    "process/createLineItemForProductIdAndTemplateId"
  )

export const fetchVNZCloudImagePage = createAction(
  "process/fetchVNZCloudImagePage"
)

export const startVNZAccessTokenRefresher = createAction(
  "process/startVNZAccessTokenRefresher"
)

export const actions = {
  queueImageForUploading,
  queueImageForProcessing,
  initStores,
  searchStores,
  updateOrderSummary,
  updateOrderSummarySuccess,
  placeOrder,
  createOrder,
  applyCouponCode,
  applyCouponCodeSuccess,
  createUpload,
  createUploadFromButton,
  createUploadFromBuyNowButton,
  createUploadFromFileResolver,
  createLineItem,
  removeLineItem,
  createLineItemWithImage,
  createLineItemFromTemplate,
  createLineItemFromButton,
  duplicateLineItem,
  updateLineItemProduct,
  updateLineItemProductTemplate,
  updateLineItemProductTemplateVariant,
  updateImageRegionImage,
  updateDocument,
  updateMissingImagesForLineItem,
  setUpdateMissingImagesDialogChoice,
  updateFulfillment,
  updateFulfillmentSuccess,
  updatedFulfillmentForPage,
  updateStore,
  updatedStoreForPage,
  updateLineItemOrientation,
  uploadUriImages,
  downloadProductTemplateTags,
  downloadProductTemplates,
  downloadProductTemplate,
  productCompatibility,
  productCompatibilityDialog,
  applyEditorChanges,
  addLineItemToCart,
  restoreCheckoutData,
  restoreCartData,
  saveAttributionData,
  bootstrapPage,
  processCloudProviderSession,
  placeReOrder,
  orderFromStore,
  insertPageSpreadToLineItemBefore,
  insertPageSpreadToLineItemAfter,
  removePageSpreadFromLineItem,
  removeIncompletePageSpreadsFromLineItem,
  swapImageRegionFromPageAToPageB,
  applyImageAITransformation,
  bootstrapProductFeed,
  uploadImagesAndCreateLineItemsForProductId,
  uploadImagesAndCreateLineItemsForProductIdAndTemplateId,
  createLineItemForProductIdWithPlaceholderImage,
  createLineItemForProductIdAndTemplateIdWithPlaceholderImage,
  createLineItemForProductId,
  createLineItemForProductIdAndTemplateId,
  createLineItemForProductIdWithImageUrl,
  replaceImageRegionImageWithImageUrl,
  fillLineItemImageRegionsWithImageUrls,
  fetchVNZCloudImagePage,
  logoutVNZ: VNZAuth.actions.logout,
}
